import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Check, Sparkles, Loader2 } from "lucide-react";

const PLANS = [
  {
    key: "grantflow_monthly",
    name: "Pro Monthly",
    price: "$100",
    period: "/month",
    tagline: "Try it. Cancel anytime.",
    features: [
      "40+ curated grants across all 50 states",
      "Unlimited AI-drafted documents (Claude Sonnet 5)",
      "Application tracker with deadline autopilot",
      "Auto-submit reminders + calendar sync",
      "Academy access & templates",
      "Priority email support",
    ],
    highlight: false,
  },
  {
    key: "grantflow_yearly",
    name: "Pro Annual",
    price: "$960",
    period: "/year",
    tagline: "Save $240 — 20% off",
    features: [
      "Everything in Pro Monthly",
      "2 free months included",
      "Locked-in price for 12 months",
      "Priority support + private Slack",
      "Quarterly live grant reviews",
    ],
    highlight: true,
  },
];

export default function Pricing() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState("");

  const subscribe = async (lookup_key) => {
    if (!user) { nav("/signup"); return; }
    setLoading(lookup_key);
    try {
      const { data } = await api.post("/api/payments/checkout", {
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
    } finally { setLoading(""); }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <section className="max-w-6xl mx-auto px-5 lg:px-8 py-16 text-center" data-testid="pricing-section">
        <div className="overline mb-3">PRICING</div>
        <h1 className="font-display text-5xl md:text-6xl font-bold tracking-tight">One plan. Everything included.</h1>
        <p className="mt-4 text-neutral-600 max-w-2xl mx-auto text-lg">One awarded grant covers a decade of membership. Save 20% with the annual plan.</p>

        <div className="mt-12 grid md:grid-cols-2 gap-6 text-left">
          {PLANS.map((p) => (
            <div key={p.key} className={`gf-card p-8 relative ${p.highlight ? "border-neutral-900 shadow-lg" : ""}`} data-testid={`plan-${p.key}`}>
              {p.highlight && (
                <div className="absolute -top-3 left-8 bg-[color:var(--accent)] text-white text-xs uppercase tracking-widest px-3 py-1 rounded-full">Best value</div>
              )}
              <h3 className="font-display text-2xl font-semibold">{p.name}</h3>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="font-display text-5xl font-bold tracking-tight">{p.price}</span>
                <span className="text-neutral-500">{p.period}</span>
              </div>
              <p className="text-neutral-600 text-sm mt-2">{p.tagline}</p>
              <ul className="mt-6 space-y-3">
                {p.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-neutral-800">
                    <Check size={16} className="text-emerald-600 mt-0.5 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <button onClick={() => subscribe(p.key)} disabled={!!loading}
                className={`mt-8 w-full justify-center ${p.highlight ? "btn-accent" : "btn-primary"}`}
                data-testid={`subscribe-${p.key}`}>
                {loading === p.key ? <><Loader2 size={16} className="animate-spin" /> Redirecting…</> : <><Sparkles size={16} /> Subscribe</>}
              </button>
            </div>
          ))}
        </div>

        <div className="mt-14 max-w-2xl mx-auto text-xs text-neutral-500">
          Secure Stripe checkout. Multiple payment methods (card, wallet, and crypto where enabled). Cancel anytime from your Stripe billing portal.
        </div>
      </section>
      <Footer />
    </div>
  );
}
